import { atom, type Atom } from 'jotai';
import { withAtomEffect } from 'jotai-effect';

/**
 * 탭이 숨겨진(`document.hidden`) 동안에만 `task`를 `periodMs` 간격으로 반복 실행한다.
 *
 * 브라우저는 숨은 탭에서 requestAnimationFrame을 멈추므로, rAF 기반 스로틀이 얼어붙는
 * 그 구간을 메우는 fallback 스케줄러. atomWithRafThrottle의 forceRefreshPeriod가 유일 소비자지만
 * 부수효과(타이머·DOM)라 순수함수는 아니며, jotai 없이 단독으로 단위 테스트할 수 있게 분리해 둔다.
 * - 탭이 보이는 동안(foreground)에는 돌지 않는다 → rAF 스로틀을 방해하지 않는다.
 * - `setInterval`이 아니라 재귀 `setTimeout`이라, 백그라운드 타이머 throttling으로 콜백이 밀려도
 *   직전 실행이 끝난 뒤에만 다음을 예약한다 → 콜백이 쌓였다 몰아서 발화(pile-up)하지 않는다.
 * - `visibilitychange`로 자동 start/stop. 시작 시 이미 숨겨져 있으면 바로 스케줄한다.
 *
 * @returns 정리 함수(리스너 제거 + 타이머 취소). `document`가 없는 환경(SSR)에서는 no-op.
 */
export function runWhileHidden(periodMs: number, task: () => void): () => void {
  if (typeof document === 'undefined') return () => {};

  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const stop = () => {
    if (timeoutId != null) clearTimeout(timeoutId);
    timeoutId = undefined;
  };

  const tick = () => {
    if (!document.hidden) {
      stop(); // 실행 직전 다시 보이게 됐으면 멈춘다 (방어)
      return;
    }
    task();
    timeoutId = setTimeout(tick, periodMs); // flush 완료 후에만 다음 예약
  };

  const sync = () => {
    if (document.hidden) {
      if (timeoutId == null) timeoutId = setTimeout(tick, periodMs);
    } else {
      stop();
    }
  };

  sync();
  document.addEventListener('visibilitychange', sync);
  return () => {
    document.removeEventListener('visibilitychange', sync);
    stop();
  };
}

/**
 * source atom의 변경을 프레임당 최대 1회(requestAnimationFrame)로 병합한 read-only atom을 만든다.
 *
 * 소켓 tick처럼 프레임 레이트(≈16.6ms)보다 잦게 갱신되는 "표시 전용" 값의 리렌더를
 * 프레임당 1회로 캡핑하는 용도. 프레임 안에 여러 번 갱신돼도 마지막 값 하나만 전파된다.
 *
 * 주의:
 * - 반환 atom의 변수명에는 반드시 `throttled` prefix를 붙일 것 (예: throttledOrderbookAtom).
 *   사용처에서 1프레임 지연 가능성이 드러나게 하기 위한 컨벤션이다 (AGENTS.md State Management 참고).
 * - 표시값이 최대 1프레임 지연된다. 주문 제출·검증 등 정확성이 필요한 계산 경로에는
 *   반드시 원본(source) atom을 사용할 것.
 * - 구독자가 없을 때(store.get 단발 읽기, SSR)는 source 값을 그대로 통과시킨다.
 *
 * @param sourceAtom 스로틀할 원본 atom
 * @param forceRefreshPeriod (선택, ms) 지정하면 **탭이 숨겨진(document.hidden) 동안에만** 이 주기마다
 *   최신 source 값을 강제로 flush한다. 브라우저는 숨은 탭에서 rAF를 멈추므로 rAF만으로는 값이 얼어붙는데,
 *   그 구간을 이 타이머가 메운다. 탭이 보이는 동안(foreground)에는 타이머가 돌지 않아 순수 rAF 스로틀이
 *   그대로 유지된다(프레임 병합 우회 없음). "숨은 탭에서도 갱신돼야 하는" 값(예: 브라우저 탭 제목에 노출되는
 *   가격)에만 켤 것. 미설정(기본)이면 숨은 탭에서 자연히 멈춰 보이지 않는 UI의 불필요한 연산을 막는다.
 */
export function atomWithRafThrottle<T>(sourceAtom: Atom<T>, forceRefreshPeriod?: number): Atom<T> {
  // "아직 flush 안 됨"과 "값이 undefined임"을 구분하기 위한 센티넬 (T가 undefined일 수 있으므로 null로는 부족)
  const EMPTY = Symbol('empty');
  const heldAtom = withAtomEffect(atom<T | typeof EMPTY>(EMPTY), (get, set) => {
    const latest = get(sourceAtom); // source만 구독 — 변경마다 effect 재실행
    // heldAtom을 effect 안에서 get으로 읽지 않으므로(출력 전용) write가 effect를 self-retrigger하지 않는다.
    // 같은 값이면 jotai의 Object.is bailout이 전파를 끊는다 → 별도 dedup 가드 불필요.
    const flush = () => set(heldAtom, latest);
    // 최초 mount(또는 rAF 부재 환경)에는 즉시 반영 — 이후 프레임 전 읽기가 live source로 새지 않게 한다.
    if (get.peek(heldAtom) === EMPTY || typeof requestAnimationFrame !== 'function') {
      flush();
      return;
    }
    const rafId = requestAnimationFrame(flush);
    return () => cancelAnimationFrame(rafId);
  });

  // (opt-in) 탭이 숨겨진 동안에만 최신 source 값을 강제로 flush한다(스케줄링 상세는 runWhileHidden 참고).
  // source를 구독하지 않는 별도 effect라 heldAtom write가 self-retrigger를 일으키지 않는다(peek로만 읽음).
  const forceRefreshAtom =
    forceRefreshPeriod == null
      ? null
      : withAtomEffect(atom(null), (_get, set) =>
          runWhileHidden(forceRefreshPeriod, () => set(heldAtom, _get.peek(sourceAtom))),
        );

  return atom((get) => {
    if (forceRefreshAtom) get(forceRefreshAtom); // opt-in일 때만 interval effect를 mount시킨다
    const held = get(heldAtom);
    return held === EMPTY ? get(sourceAtom) : held;
  });
}
